import type { ThemeColorName } from '@examdojo/styles';
import type { GeometryElement } from 'jsxgraph';
import type { JsxgraphThemeService } from '../../jsxgraph-theme.service';
import type { ElementCreatorContext } from '../core/board.types';
import { deepMerge } from '../core/deepMerge';
import { bindLabel, createCurveLabelContext } from '../label/label.bind';
import { resolveLabelConfig } from '../label/label.model';
import type { CurveStyle, ElementTheme } from '../theme/theme.model';
import type { CurveArgs, CurveResult, DataCurveArgs, FunctionGraphCurveArgs, ParametricCurveArgs } from './curve.model';

/**
 * Resolve a theme color name to a CSS color string.
 */
function resolveColor(themeService: JsxgraphThemeService, color: ThemeColorName | undefined): string | undefined {
  if (!color) {
    return undefined;
  }
  return themeService.getColor(color);
}

/**
 * Build JSXGraph curve attributes from style configuration.
 */
function buildCurveAttributes(
  themeService: JsxgraphThemeService,
  baseStyle: ElementTheme['curve'],
  styleOverrides?: CurveStyle,
): Record<string, unknown> {
  const style = deepMerge(baseStyle, styleOverrides);
  const strokeColor = resolveColor(themeService, style.color);

  const attrs: Record<string, unknown> = {
    strokeColor,
    strokeWidth: style.strokeWidth,
    dash: style.dash,
    visible: styleOverrides?.visible ?? true,
    withLabel: false,
  };

  if (style.arrows?.first) {
    attrs['firstArrow'] =
      typeof style.arrows.first === 'object'
        ? style.arrows.first
        : { type: style.arrows.type, size: style.arrows.size };
  }
  if (style.arrows?.last) {
    attrs['lastArrow'] =
      typeof style.arrows.last === 'object' ? style.arrows.last : { type: style.arrows.type, size: style.arrows.size };
  }

  return attrs;
}

/**
 * Create a function graph curve (y = f(x)).
 * Label is configured via style.label.
 */
function createFunctionGraphCurve(
  ctx: ElementCreatorContext,
  themeService: JsxgraphThemeService,
  theme: ElementTheme,
  args: FunctionGraphCurveArgs,
): CurveResult {
  const { board } = ctx;
  const { fn, domain, name = '', style } = args;

  const attrs = buildCurveAttributes(themeService, theme.curve, style);

  // Create the function graph
  const curveArgs: unknown[] = [fn];
  if (domain) {
    curveArgs.push(domain[0], domain[1]);
  }

  const curve = board.create('functiongraph', curveArgs, attrs) as GeometryElement;

  const result: CurveResult = { curve };

  // Create label if configured - now from style.label
  const labelColor = style?.color ?? 'neutral';
  const labelConfig = resolveLabelConfig(style?.label, { color: labelColor });

  if (labelConfig && name) {
    // For function graph labels, create a glider point on the curve
    const midX = domain ? (domain[0] + domain[1]) / 2 : 0;
    const glider = board.create('glider', [midX, fn(midX), curve], { visible: false }) as GeometryElement;

    result.label = bindLabel({
      ctx,
      themeService,
      element: glider,
      config: labelConfig,
      contextProvider: createCurveLabelContext(name),
    });
  }

  return result;
}

/**
 * Create a parametric curve.
 * Label is configured via style.label.
 */
function createParametricCurve(
  ctx: ElementCreatorContext,
  themeService: JsxgraphThemeService,
  theme: ElementTheme,
  args: ParametricCurveArgs,
): CurveResult {
  const { board } = ctx;
  const { x, y, range, name = '', style } = args;

  const attrs = buildCurveAttributes(themeService, theme.curve, style);

  // Create the parametric curve
  const curve = board.create('curve', [x, y, range[0], range[1]], attrs) as GeometryElement;

  const result: CurveResult = { curve };

  // Create label if configured - now from style.label
  const labelColor = style?.color ?? 'neutral';
  const labelConfig = resolveLabelConfig(style?.label, { color: labelColor });

  if (labelConfig && name) {
    // For parametric labels, create a point at midpoint of parameter range
    const midT = (range[0] + range[1]) / 2;
    const labelPoint = board.create('point', [() => x(midT), () => y(midT)], { visible: false }) as GeometryElement;

    result.label = bindLabel({
      ctx,
      themeService,
      element: labelPoint,
      config: labelConfig,
      contextProvider: createCurveLabelContext(name),
    });
  }

  return result;
}

/**
 * Create a data curve from x/y arrays.
 * Label is configured via style.label.
 */
function createDataCurve(
  ctx: ElementCreatorContext,
  themeService: JsxgraphThemeService,
  theme: ElementTheme,
  args: DataCurveArgs,
): CurveResult {
  const { board } = ctx;
  const { xData, yData, name = '', style } = args;

  const attrs = buildCurveAttributes(themeService, theme.curve, style);

  // Create the data curve
  const curve = board.create('curve', [xData, yData], attrs) as GeometryElement;

  const result: CurveResult = { curve };

  // Create label if configured - now from style.label
  const labelColor = style?.color ?? 'neutral';
  const labelConfig = resolveLabelConfig(style?.label, { color: labelColor });

  if (labelConfig && name && xData.length > 0) {
    // For data labels, create a point at the middle data point
    const midIndex = Math.floor(xData.length / 2);
    const labelPoint = board.create('point', [xData[midIndex], yData[midIndex]], { visible: false }) as GeometryElement;

    result.label = bindLabel({
      ctx,
      themeService,
      element: labelPoint,
      config: labelConfig,
      contextProvider: createCurveLabelContext(name),
    });
  }

  return result;
}

/**
 * Create a curve element with optional label.
 *
 * Supports three curve types:
 * - functiongraph: y = f(x)
 * - parametric: x(t), y(t) over range [t0, t1]
 * - data: arrays of x and y values
 *
 * @param ctx - Element creator context (board, container)
 * @param themeService - Theme service for color resolution
 * @param theme - Element theme with defaults
 * @param args - Curve creation arguments
 * @returns Curve result with element and optional label
 */
export function createCurve(
  ctx: ElementCreatorContext,
  themeService: JsxgraphThemeService,
  theme: ElementTheme,
  args: CurveArgs,
): CurveResult {
  switch (args.type) {
    case 'functiongraph':
      return createFunctionGraphCurve(ctx, themeService, theme, args);
    case 'parametric':
      return createParametricCurve(ctx, themeService, theme, args);
    case 'data':
      return createDataCurve(ctx, themeService, theme, args);
  }
}
